import React, { useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// 2023 data from Excel Q17&18 rows 17-21, column 1
const labels2023 = ['Alimentos', 'Construcción', 'Retail', 'Servicios Logísticos', 'No mide'];
const values2023 = [2, 2, 6, 6, 84];

// 2024 data from Excel Q17&18 rows 17-23, columns 15-16
const labels2024 = ['Alimentos', 'Automotriz', 'Construcción', 'Manufactura', 'Retail', 'Servicios Logísticos', 'No mide'];
const values2024 = [1.6, 0.8, 1.6, 2.3, 3.9, 5.5, 84];

// 2025 data from Excel Q17&18 rows 5-12, column 3 (% "Sí mide" per industry) + Total "No mide"
const labels2025 = ['Sector salud/ Farmacéuticas', 'Construcción', 'Automotriz', 'Manufactura', 'Servicios Logísticos', 'Alimentos', 'Retail', 'No mide'];
const values2025 = [3, 4, 3, 8, 7, 7, 4, 63];

const colorMap = {
  'Sector salud/ Farmacéuticas': '#0F2233',
  'Alimentos': '#172B3E',
  'Automotriz': '#1A3A52',
  'Construcción': '#A2AEBB',
  'Manufactura': '#2E5A7C',
  'Retail': '#1F3D58',
  'Servicios Logísticos': '#476A8E',
  'No mide': '#B0B0B0',
};

// Pick a black/white data-label color based on the segment's background luminance,
// so labels stay legible on both dark and light slices.
const hexLuminance = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b;
};
const labelColorFor = (label) => (hexLuminance(colorMap[label] || '#A2AEBB') > 150 ? '#000' : '#fff');

const getChartData = (labels, values, year) => ({
  labels,
  datasets: [
    {
      label: year,
      data: values,
      backgroundColor: labels.map((label) => colorMap[label] || '#A2AEBB'),
      borderColor: '#fff',
      borderWidth: 2,
    },
  ],
});

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(31, 61, 88, 0.9)',
      titleFont: { size: 12, weight: 'bold' },
      bodyFont: { size: 11 },
      padding: 10,
      cornerRadius: 6,
      callbacks: {
        label: (context) => `${context.label}: ${context.raw}%`,
      },
    },
    datalabels: {
      color: (context) => labelColorFor(context.chart.data.labels[context.dataIndex]),
      font: { weight: 'bold', size: 10 },
      formatter: (value, context) => {
        const label = context.chart.data.labels[context.dataIndex];
        if (value >= 5) {
          return `${label}\n${value}%`;
        }
        return value >= 1 ? `${value}%` : '';
      },
      anchor: 'center',
      align: 'center',
      textAlign: 'center',
    },
  },
};

const HuellaPie = () => {
  const [infoOpen, setInfoOpen] = useState(false);

  const chartData2023 = getChartData(labels2023, values2023, '2023');
  const chartData2024 = getChartData(labels2024, values2024, '2024');
  const chartData2025 = getChartData(labels2025, values2025, '2025');

  const infoContent = {
    formula:
      'Porcentaje = (Empresas que miden por industria / Total de empresas que miden) × 100',
    description:
      'Muestra la cantidad de emisiones de gases de efecto invernadero que se emiten a la atmósfera por medio de alguna actividad humana, que puede ser un producto o un servicio.',
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={1}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="h6" align="center" sx={{ mb: 2, fontWeight: 'bold', color: '#A2AEBB' }}>
            2023
          </Typography>
          <div style={{ height: '350px' }}>
            <Doughnut data={chartData2023} options={options} />
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="h6" align="center" sx={{ mb: 2, fontWeight: 'bold', color: '#476A8E' }}>
            2024
          </Typography>
          <div style={{ height: '350px' }}>
            <Doughnut data={chartData2024} options={options} />
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="h6" align="center" sx={{ mb: 2, fontWeight: 'bold', color: '#1F3D58' }}>
            2025
          </Typography>
          <div style={{ height: '350px' }}>
            <Doughnut data={chartData2025} options={options} />
          </div>
        </Grid>
      </Grid>

      {/* Shared Legend */}
      <Box display="flex" justifyContent="center" flexWrap="wrap" mt={2} gap={1}>
        {Object.entries(colorMap).map(([label, color]) => (
          <Box key={label} display="flex" alignItems="center" mx={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: color,
                borderRadius: '50%',
                mr: 0.5,
                border: '1px solid #ccc',
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '11px' }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HuellaPie;
